/**
 * Vista Dashboard de FinVoz (FR-DASH-1..8, spec/01-requirements.md §6).
 *
 * Muestra:
 *  - Patrimonio neto (Activos - Pasivos) derivado exclusivamente del libro (INV-4)
 *  - Ingresos, gastos y resultado neto del periodo (FR-DASH-2, FR-DASH-8)
 *  - Desglose de gastos por categoria con porcentaje (FR-DASH-3)
 *  - Estado de presupuestos y metas (FR-DASH-4)
 *  - Trazabilidad hacia los asientos que lo componen (FR-DASH-5)
 *  - Indicacion clara de fecha de cotizaciones de mercado (FR-DASH-6)
 */

import type { AppState } from "../types.js";
import { formatMinorUnits, formatMoney } from "../format.js";
import { escapeHtml } from "../sanitize.js";
import { accountBalance } from "../../core/ledger.js";
import { money, addMoney } from "../../core/money.js";
import type { Entry } from "../../core/entry.js";

function filterEntriesByPeriod(entries: readonly { entry: Entry }[], period: AppState["selectedPeriod"]): Entry[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed

  return entries
    .map((e) => e.entry)
    .filter((entry) => {
      if (period === "all") return true;
      const [yStr, mStr] = entry.date.split("-");
      const entryYear = Number(yStr);
      const entryMonth = Number(mStr) - 1;

      if (period === "year") return entryYear === currentYear;
      if (period === "month") return entryYear === currentYear && entryMonth === currentMonth;
      if (period === "quarter") {
        const currentQuarter = Math.floor(currentMonth / 3);
        const entryQuarter = Math.floor(entryMonth / 3);
        return entryYear === currentYear && entryQuarter === currentQuarter;
      }
      return true;
    });
}

export function renderDashboard(state: AppState, onNavigate: (tab: AppState["activeTab"]) => void): string {
  const currency = state.currency || "PEN";
  const scale = 2;
  const rawEntries = state.entries.map((e) => e.entry);
  const periodEntries = filterEntriesByPeriod(state.entries, state.selectedPeriod);

  // 1. Patrimonio Neto: Total Activos - Total Pasivos (todos los tiempos)
  let totalAssetsMinor = 0n;
  let totalLiabilitiesMinor = 0n;

  for (const acc of state.accounts) {
    if (acc.archived) continue;
    const bal = accountBalance(acc.accountId, rawEntries, currency, scale);
    if (acc.type === "asset") {
      totalAssetsMinor += bal.amountMinor;
    } else if (acc.type === "liability") {
      totalLiabilitiesMinor += bal.amountMinor;
    }
  }

  const netWorthMinor = totalAssetsMinor - totalLiabilitiesMinor;

  // 2. Ingresos y Gastos del periodo seleccionado
  let periodIncomeMinor = 0n;
  let periodExpenseMinor = 0n;
  const categoryExpenses: { accountId: string; name: string; amountMinor: bigint }[] = [];

  for (const acc of state.accounts) {
    if (acc.archived) continue;
    if (acc.type === "income") {
      const bal = accountBalance(acc.accountId, periodEntries, currency, scale);
      // En contabilidad de partida doble los ingresos tienen saldo acreedor (negativo en balance contable puro o credito)
      const absAmount = bal.amountMinor < 0n ? -bal.amountMinor : bal.amountMinor;
      periodIncomeMinor += absAmount;
    } else if (acc.type === "expense") {
      const bal = accountBalance(acc.accountId, periodEntries, currency, scale);
      const amount = bal.amountMinor > 0n ? bal.amountMinor : 0n;
      periodExpenseMinor += amount;
      if (amount > 0n) {
        categoryExpenses.push({
          accountId: acc.accountId,
          name: acc.name,
          amountMinor: amount,
        });
      }
    }
  }

  const netResultMinor = periodIncomeMinor - periodExpenseMinor;

  // Ordenar categorias de gasto de mayor a menor (FR-DASH-3)
  categoryExpenses.sort((a, b) => (b.amountMinor > a.amountMinor ? 1 : -1));

  const periodLabels: Record<AppState["selectedPeriod"], string> = {
    month: "Este Mes",
    quarter: "Este Trimestre",
    year: "Este Año",
    all: "Histórico Completo",
  };

  return `
    <div class="dashboard-view" data-testid="dashboard-view">
      <!-- Fila Superior: Patrimonio Neto y Selector de Periodo -->
      <div class="card">
        <div class="card-header">
          <div>
            <span class="metric-label">Patrimonio Neto Total</span>
            <div class="metric-value ${netWorthMinor >= 0n ? 'metric-positive' : 'metric-negative'}" data-testid="net-worth-value">
              ${formatMinorUnits(netWorthMinor, currency, scale)}
            </div>
            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 4px;">
              Activos: ${formatMinorUnits(totalAssetsMinor, currency, scale)} | Pasivos: ${formatMinorUnits(totalLiabilitiesMinor, currency, scale)}
            </div>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <label for="period-select" style="font-size: 0.85rem; color: var(--text-secondary);">Periodo:</label>
            <select id="period-select" data-testid="period-select" aria-label="Seleccionar periodo">
              <option value="month" ${state.selectedPeriod === "month" ? "selected" : ""}>Este Mes</option>
              <option value="quarter" ${state.selectedPeriod === "quarter" ? "selected" : ""}>Este Trimestre</option>
              <option value="year" ${state.selectedPeriod === "year" ? "selected" : ""}>Este Año</option>
              <option value="all" ${state.selectedPeriod === "all" ? "selected" : ""}>Todo</option>
            </select>
          </div>
        </div>
      </div>

      <!-- KPIs del Periodo -->
      <div class="grid-3">
        <div class="card" style="border-left: 4px solid var(--success); cursor: pointer;" data-action="filter-ledger" data-type="income">
          <span class="metric-label">Ingresos (${periodLabels[state.selectedPeriod]})</span>
          <div class="metric-value metric-positive" data-testid="kpi-income">
            ${formatMinorUnits(periodIncomeMinor, currency, scale)}
          </div>
          <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 6px;">Ver asientos asociados &rarr;</div>
        </div>

        <div class="card" style="border-left: 4px solid var(--danger); cursor: pointer;" data-action="filter-ledger" data-type="expense">
          <span class="metric-label">Gastos (${periodLabels[state.selectedPeriod]})</span>
          <div class="metric-value metric-negative" data-testid="kpi-expense">
            ${formatMinorUnits(periodExpenseMinor, currency, scale)}
          </div>
          <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 6px;">Ver asientos asociados &rarr;</div>
        </div>

        <div class="card" style="border-left: 4px solid ${netResultMinor >= 0n ? 'var(--accent)' : 'var(--warning)'}; cursor: pointer;" data-action="navigate" data-tab="ledger">
          <span class="metric-label">Resultado Neto</span>
          <div class="metric-value ${netResultMinor >= 0n ? 'metric-positive' : 'metric-negative'}" data-testid="kpi-net">
            ${formatMinorUnits(netResultMinor, currency, scale)}
          </div>
          <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 6px;">
            ${netResultMinor >= 0n ? 'Superávit en el periodo' : 'Déficit en el periodo'}
          </div>
        </div>
      </div>

      <!-- Fila 2: Gastos por Categoria y Presupuestos Activos -->
      <div class="grid-2">
        <!-- Desglose de Gastos -->
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Gastos por Categoría (${periodLabels[state.selectedPeriod]})</h2>
          </div>
          ${categoryExpenses.length === 0
            ? `<div style="color: var(--text-muted); text-align: center; padding: 24px 0;">No hay gastos registrados en este periodo.</div>`
            : `
              <div class="category-list">
                ${categoryExpenses.map((cat) => {
                  const pct = periodExpenseMinor > 0n ? Number((cat.amountMinor * 100n) / periodExpenseMinor) : 0;
                  return `
                    <div style="margin-bottom: 12px;">
                      <div style="display: flex; justify-content: space-between; font-size: 0.9rem;">
                        <span>${escapeHtml(cat.name)}</span>
                        <span style="font-weight: 600;">${formatMinorUnits(cat.amountMinor, currency, scale)} (${pct}%)</span>
                      </div>
                      <div class="progress-bar-bg">
                        <div class="progress-bar-fill" style="width: ${Math.min(pct, 100)}%;"></div>
                      </div>
                    </div>
                  `;
                }).join("")}
              </div>
            `}
        </div>

        <!-- Presupuestos y Metas -->
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Presupuestos y Metas</h2>
            <button class="btn-secondary" style="font-size: 0.8rem; padding: 4px 10px;" data-action="navigate" data-tab="budgets">Gestionar</button>
          </div>
          ${state.budgets.length === 0 && state.goals.length === 0
            ? `<div style="color: var(--text-muted); text-align: center; padding: 24px 0;">No hay presupuestos ni metas configurados.</div>`
            : `
              <div>
                ${state.budgets.map((b) => {
                  const acc = state.accounts.find((a) => a.accountId === b.accountId);
                  const consumed = accountBalance(b.accountId, periodEntries, currency, scale).amountMinor;
                  const pct = b.limitMinor > 0n ? Number((consumed * 100n) / b.limitMinor) : 0;
                  const isExceeded = consumed > b.limitMinor;
                  const isWarning = pct >= b.thresholdPct;
                  return `
                    <div style="margin-bottom: 14px;">
                      <div style="display: flex; justify-content: space-between; font-size: 0.85rem;">
                        <span style="font-weight: 500;">Presupuesto: ${escapeHtml(acc?.name ?? b.accountId)}</span>
                        <span style="color: ${isExceeded ? 'var(--danger)' : isWarning ? 'var(--warning)' : 'var(--text-secondary)'};">
                          ${formatMinorUnits(consumed, currency, scale)} / ${formatMinorUnits(b.limitMinor, currency, scale)}
                          ${isExceeded ? ' [¡SUPERADO!]' : isWarning ? ' [Alerta]' : ''}
                        </span>
                      </div>
                      <div class="progress-bar-bg">
                        <div class="progress-bar-fill" style="width: ${Math.min(pct, 100)}%; background-color: ${isExceeded ? 'var(--danger)' : isWarning ? 'var(--warning)' : 'var(--accent)'};"></div>
                      </div>
                    </div>
                  `;
                }).join("")}

                ${state.goals.map((g) => {
                  const saved = accountBalance(g.accountId, rawEntries, currency, scale).amountMinor;
                  const pct = g.targetMinor > 0n ? Number((saved * 100n) / g.targetMinor) : 0;
                  return `
                    <div style="margin-bottom: 14px; padding-top: 8px; border-top: 1px solid var(--border);">
                      <div style="display: flex; justify-content: space-between; font-size: 0.85rem;">
                        <span style="font-weight: 500;">Meta: ${escapeHtml(g.name)}</span>
                        <span>${formatMinorUnits(saved, currency, scale)} / ${formatMinorUnits(g.targetMinor, currency, scale)} (${pct}%)</span>
                      </div>
                      <div class="progress-bar-bg">
                        <div class="progress-bar-fill" style="width: ${Math.min(pct, 100)}%; background-color: var(--success);"></div>
                      </div>
                      <div style="font-size: 0.75rem; color: var(--text-muted);">Fecha objetivo: ${escapeHtml(g.targetDate)}</div>
                    </div>
                  `;
                }).join("")}
              </div>
            `}
        </div>
      </div>
    </div>
  `;
}
